// Marketing backend tRPC client for the dashboard.
//
// This file ships in the Stage 2 PR ahead of any UI. The actual broadcasts
// page lives in a follow-up UI PR; once it lands, it imports the wrapper
// functions exported here.
//
// CONVENTIONS (mirror packages/dashboard/src/api/dashboardAssistantHistory.ts):
//   - The proxy client is intentionally TYPED AS `any` rather than
//     `MarketingRouter`. Dashboard's tsconfig uses
//     `moduleResolution: "node"` (legacy) which can't resolve the subpath
//     `exports` the marketing-backend uses internally (`@charitystack/
//     schemas/marketing/broadcasts`). If we typed the proxy via a
//     `MarketingRouter` import, tsc would follow into backends/marketing/*
//     and surface ~10 spurious resolution errors. Same root cause as the
//     dashboard-assistant TODO in monorepo CLAUDE.md (move types to
//     `contracts/<service>/` workspaces). Until that migration, callers
//     type their args via `@charitystack/schemas` directly.
//   - Cached client at module scope.
//   - `Authorization: <token>` (no `Bearer ` prefix) — matches what
//     `getAuthToken()` returns. The marketing backend's clerk-auth
//     middleware strips an optional `Bearer ` prefix for forward compat.
//   - `cache: 'no-store'` fetch override so tRPC GETs aren't HTTP-cached.

import { createTRPCProxyClient, httpBatchLink, httpLink, splitLink, TRPCClientError } from '@trpc/client';
import posthog from 'posthog-js';

import { getAuthToken } from '../utils/auth';
import {
  audienceMembersInputSchema,
  audienceMembersOutputSchema,
  audienceLoadInputSchema,
  nativeAudienceCatalogOutputSchema,
  nativeAudienceCreateInputSchema,
  nativeAudienceMutationResultSchema,
  nativeAudienceRecordSchema,
  nativeAudienceUpdateInputSchema,
  segmentPreviewExactInputSchema,
  segmentPreviewExactOutputSchema,
  segmentPreviewPeopleInputSchema,
  segmentPreviewPeopleOutputSchema,
  type SegmentPreviewPeopleInput,
  type NativeAudienceDefinition,
  type NativeAudienceCreateInput,
  type NativeAudienceUpdateInput,
} from '@charitystack/schemas';

// Mirror of @charitystack/schemas/marketing/merchant-account
// `MerchantAccountStatus`. Inlined (not imported) because dashboard does
// not currently declare @charitystack/schemas as a workspace dep — adding
// it would touch the lockfile for one type alias. Same convention as
// BroadcastListInput below.
export type MarketingTierStatus =
  | 'NOT_ENABLED'
  | 'PROVISIONING'
  | 'ACTIVE'
  | 'DISABLED'
  // PE-1594: the org's marketing entitlement ended (cancelled/downgraded). The
  // PF business was torn down; history stays visible read-only from our mirror,
  // writes are rejected server-side. Distinct from DISABLED (a reversible
  // self-pause that keeps the PF business).
  | 'READ_ONLY';
export type SenderEmail = {
  id: string;
  address: string;
  displayName: string;
  replyTo: string | null;
};
export type PhoneNumber = {
  id: string;
  phoneNumber: string;
};
// PE-1766 — one SMS-number request in the org's history (mirror of the
// schemas smsNumberRequestSchema read model).
//   PENDING  — registration + provisioning submitted; PF decision pending
//              (~72 business hours, approval not guaranteed).
//   ACTIVE   — the assigned number appeared and was attributed to this request.
//   REJECTED — PF rejected it (relayed by ops); rejectionReason is verbatim.
export type SmsNumberRequestStatus = 'PENDING' | 'ACTIVE' | 'REJECTED';
export type SmsNumberRequest = {
  requestId: string;
  // Preferred 3-digit area codes submitted with the request ([] = PF picks).
  prefixes: string[];
  requestedAt: string;
  status: SmsNumberRequestStatus;
  assignedPhoneNumberId?: string;
  assignedPhoneNumber?: string;
  resolvedAt?: string;
  rejectionReason?: string;
};
export type DnsRecord = {
  type: string | null;
  name: string | null;
  value: string | null;
};
export type CustomDomain = {
  id: string;
  domain: string;
  validated: boolean;
  dnsRecords: DnsRecord[];
};
export type MerchantAccountStatus = {
  organizationId: string;
  status: MarketingTierStatus;
  pocketflowBusinessId: string | null;
  pocketflowLastSyncedAt: string | null;
  // Aggregate count of contacts whose last sync attempt was permanently
  // rejected by PocketFlows (after the retry-with-omitted-field pass).
  // Null when no sync run has completed yet for this org. Drives the
  // "X contacts didn't sync" warning in MarketingSettings.
  pocketflowFailedContactsCount: number | null;
  // Per-category breakdown of the count above (PE-1684): permanent rejections
  // on the contact's email address vs phone number (`other` = rejection naming
  // no recognizable field). Each category equals the rows the matching
  // `?syncError=<type>` Contacts filter shows. Optional/absent until the
  // marketing backend deploy that writes it; null until the org's next
  // seed/drain run records the breakdown.
  pocketflowFailedContactsCounts?: { email: number; phone: number; other: number } | null;
  // Live PF data — present when status === ACTIVE; empty arrays otherwise.
  senderEmails: SenderEmail[];
  phoneNumbers: PhoneNumber[];
  customDomains: CustomDomain[];
  // When the org requested an SMS sending number. Numbers are assigned
  // manually by PocketFlows/Telgorithm, so the UI shows "Requested"
  // until phoneNumbers turns non-empty. Null = never requested.
  // LEGACY (PE-1766) — the request-history list below supersedes it; kept
  // for the fallback branch while the backend deploy catches up.
  smsNumberRequestedAt: string | null;
  // PE-1766 — the org's SMS-number request history, newest first. Absent
  // until the marketing backend deploy that writes it; the SMS tab falls
  // back to the legacy smsNumberRequestedAt branches when undefined.
  smsNumberRequests?: SmsNumberRequest[];
  // PE-1594 Banner A (cancelled-but-active). Written by pricing when the org
  // cancels but is still in its paid window. Optional/absent until pricing
  // writes them (additive). When cancelAtPeriodEnd is true, the marketing pages
  // render the "Marketing ends <currentPeriodEnd> — full access until then"
  // heads-up with a Resume CTA.
  cancelAtPeriodEnd?: boolean;
  currentPeriodEnd?: string;
};

const MARKETING_TRPC_URL = import.meta.env.VITE_APP_MARKETING_BACKEND_TRPC_URL || '';

function normalizeBaseUrl(url: string): string {
  return url.replace(/\/$/, '');
}

export function isMarketingBackendEnabled(): boolean {
  return Boolean(MARKETING_TRPC_URL);
}

// Segment previews (and the catalog) carry a whole rule definition in their
// input. A tRPC query sends its input in the GET URL, and past ~8 KB CloudFront
// refuses the request outright (QA F-06, at about 70 rules). Those three queries
// go as POST, but only when the URL would pass this length, so every ordinary
// request is unchanged; the GET batch link splits at the same length. The
// marketing handler accepts a POSTed query (`allowMethodOverride`), and a long
// request fails today anyway, so this is safe whichever deploys first.
export const MAX_GET_URL_LENGTH = 6000;
const LONG_INPUT_QUERIES = new Set(['segments.previewExact', 'segments.previewPeople', 'segments.nativeCatalog']);

/** True when this operation goes as a POST: one of the long-input queries, and its GET URL would be too long. */
export function sendsQueryAsPost(url: string, op: { type: string; path: string; input: unknown }): boolean {
  if (op.type !== 'query' || !LONG_INPUT_QUERIES.has(op.path)) return false;
  // The same shape the batch link would put in the URL for this one operation.
  const getUrl = `${url}/${op.path}?batch=1&input=${encodeURIComponent(JSON.stringify({ 0: op.input }))}`;
  return getUrl.length > MAX_GET_URL_LENGTH;
}

// Keep this untyped so the dashboard package does not import backend-only
// router modules. See file header for rationale.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
let cachedClient: any = null;

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function getMarketingClient(): any {
  const url = normalizeBaseUrl(MARKETING_TRPC_URL);
  if (!url) {
    throw new Error(
      'Marketing backend is not configured. Set VITE_APP_MARKETING_BACKEND_TRPC_URL in the dashboard env (Amplify Console or .env-cmdrc.json).',
    );
  }

  if (!cachedClient) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    cachedClient = createTRPCProxyClient<any>({
      links: [
        splitLink({
          condition: op => op.type === 'mutation' && ['segments.createFromPreset', 'broadcasts.createAudienceDraft', 'segments.createNative', 'segments.updateNative'].includes(op.path),
          true: httpLink({
          url,
          async headers({ op }) {
            const token = await getAuthToken();
            const posthogDistinctId = posthog?.get_distinct_id?.();
            const posthogSessionId = posthog?.get_session_id?.();
            const key = op.context.idempotencyKey;
            if (typeof key !== 'string' || !/^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(key)) throw new Error('A valid segment request ID is required.');
            return {
              'Idempotency-Key': key,
              // Send the Clerk token in BOTH headers: `Authorization` for
              // local/non-OAC paths, and `x-cs-authorization` which survives the
              // CloudFront OAC SigV4 signing that overwrites `Authorization` on
              // the way to the Lambda (PE-1719). The marketing clerk-auth
              // middleware prefers `x-cs-authorization`.
              ...(token
                ? { Authorization: token, 'x-cs-authorization': token }
                : {}),
              ...(posthogDistinctId
                ? { 'x-posthog-distinct-id': posthogDistinctId }
                : {}),
              ...(posthogSessionId
                ? { 'x-posthog-session-id': posthogSessionId }
                : {}),
            };
          },
          // Disable HTTP-level caching of GETs. tRPC sends queries as GET
          // by default; without `cache: 'no-store'` the browser serves
          // stale responses intermittently. Same fix as the assistant
          // thread polling — see dashboardAssistantHistory.ts.
          fetch(input, init) {
            return fetch(input, { ...init, cache: 'no-store' });
          },
        }),
          false: splitLink({
            condition: op => sendsQueryAsPost(url, op),
            true: httpBatchLink({ url, methodOverride: 'POST', headers: queryHeaders, fetch: noStoreFetch }),
            false: httpBatchLink({ url, maxURLLength: MAX_GET_URL_LENGTH, headers: queryHeaders, fetch: noStoreFetch }),
          }),
        }),
      ],
    });
  }

  return cachedClient;
}

/** Headers for every query: the Clerk token and the PostHog ids, as the mutation link sends them. */
async function queryHeaders() {
  const token = await getAuthToken();
  const posthogDistinctId = posthog?.get_distinct_id?.();
  const posthogSessionId = posthog?.get_session_id?.();
  return {
    // Send the Clerk token in BOTH headers: `Authorization` for
    // local/non-OAC paths, and `x-cs-authorization` which survives the
    // CloudFront OAC SigV4 signing that overwrites `Authorization` on
    // the way to the Lambda (PE-1719). The marketing clerk-auth
    // middleware prefers `x-cs-authorization`.
    ...(token
      ? { Authorization: token, 'x-cs-authorization': token }
      : {}),
    ...(posthogDistinctId
      ? { 'x-posthog-distinct-id': posthogDistinctId }
      : {}),
    ...(posthogSessionId
      ? { 'x-posthog-session-id': posthogSessionId }
      : {}),
  };
}

// Disable HTTP-level caching of GETs. tRPC sends queries as GET by default; without
// `cache: 'no-store'` the browser serves stale responses intermittently. Same fix as the
// assistant thread polling — see dashboardAssistantHistory.ts.
function noStoreFetch(input: RequestInfo | URL, init?: RequestInit) {
  return fetch(input, { ...init, cache: 'no-store' });
}

// Wrapper typed via @charitystack/schemas (which dashboard CAN import —
// dep-cruiser explicitly excludes schemas from the no-FE-importing-backend
// rule). When the broadcasts UI lands, this is the function it calls.
export type BroadcastListInput = {
  cursor?: string | null;
  limit?: number;
};

export async function listBroadcasts(input: BroadcastListInput) {
  return getMarketingClient().broadcasts.list.query({
    cursor: input.cursor ?? null,
    limit: input.limit ?? 50,
  });
}

// ─────────────────────────────────────────────────────────────────────────────
// New pf_broadcasts / pf_segments mirror reads (M-1.5+)
// ─────────────────────────────────────────────────────────────────────────────

// Mirror of @charitystack/schemas/marketing/mirror types. Inlined to avoid
// adding a workspace dep — same convention as the merchant-account types
// above.
export type PfBroadcastType = 'CAMPAIGN' | 'TRIGGER';
export type PfBroadcastStatus =
  | 'draft'
  | 'scheduled'
  | 'started'
  | 'stopped'
  | 'finished'
  | 'active'
  | 'inactive';
export type PfBroadcast = {
  pocketflowsId: string;
  organizationId: string;
  organizationName: string;
  broadcastType: PfBroadcastType;
  segmentId: string | null;
  name: string;
  status: PfBroadcastStatus;
  senderEmailAddress: string | null;
  senderPhoneNumber: string | null;
  attributedRevenue: number | null;
  metadataJson: string;
  statisticsJson: string;
  scheduledAt: string | null;
  scheduledEndAt: string | null;
  projectDefinedEvent: string | null;
  internalEventTypeJson: string | null;
  pfCreatedAt: string;
  pfUpdatedAt: string;
  csMirrorSyncedAt: string | null;
};
export type PfSegment = {
  // CharityStack-owned UUID — the SegmentsTable PK, used to join back to
  // OpenSearch hits and the SegmentContactTable membership rows.
  id: string;
  pocketflowsId: string;
  organizationId: string;
  organizationName: string;
  name: string;
  description: string;
  filter: string | null;
  pfCreatedAt: string;
  pfUpdatedAt: string;
  csMirrorSyncedAt: string | null;
  // Static membership size from the SegmentContactTable junction. `null` for
  // dynamic (filter-based) segments — PF computes their membership at send
  // time, so there is no stored count (render "—", not "0").
  contactCount: number | null;
};

export async function listPfBroadcasts(opts?: {
  broadcastType?: PfBroadcastType;
}): Promise<{ items: PfBroadcast[] }> {
  return getMarketingClient().broadcasts.listMirror.query({
    broadcastType: opts?.broadcastType,
  });
}

export async function listPfSegments(): Promise<{ items: PfSegment[] }> {
  return getMarketingClient().segments.list.query({});
}

// ContactsSheet "Marketing" section — which broadcasts the contact was
// enrolled in and which segments it belongs to, served from the
// contact-lookup junction tables. `contactId` is the contactsdb id.
export type ContactEngagementBroadcast = {
  id: string;
  name: string;
  broadcastType: PfBroadcastType;
  status: PfBroadcastStatus;
  enrolledAt: string;
  // PE-1608 — whether this broadcast is currently opted-out for the contact.
  unsubscribed: boolean;
};
export type ContactEngagementSegment = {
  id: string;
  name: string;
  description: string;
};
// PE-1608 — contact-level global marketing status, surfaced alongside
// engagement so the sheet can render the consent toggle and gate the
// per-broadcast controls (which require a synced contact).
export type ContactSubscriptionStatus = {
  synced: boolean;
  subscribed: boolean;
};
export async function getContactEngagement(contactId: string): Promise<{
  subscription: ContactSubscriptionStatus;
  broadcasts: ContactEngagementBroadcast[];
  segments: ContactEngagementSegment[];
}> {
  return getMarketingClient().contacts.engagement.query({ contactId });
}

// Bulk per-contact broadcast/segment counts for the Contacts-table count
// columns. One call per table page with the visible contact ids; cheap COUNT
// queries server-side (no PF calls). Returns a map keyed by contactId.
export type ContactEngagementCount = {
  broadcastCount: number;
  segmentCount: number;
  // Global marketing consent (true = subscribed) — drives the row + bulk
  // subscribe/unsubscribe affordances.
  subscribed: boolean;
};

export async function getContactEngagementCounts(
  contactIds: string[],
): Promise<Record<string, ContactEngagementCount>> {
  if (!contactIds || contactIds.length === 0) return {};
  const res: {
    counts: Array<{ contactId: string } & ContactEngagementCount>;
  } = await getMarketingClient().contacts.engagementCounts.query({ contactIds });
  const map: Record<string, ContactEngagementCount> = {};
  for (const c of res.counts ?? []) {
    map[c.contactId] = {
      broadcastCount: c.broadcastCount,
      segmentCount: c.segmentCount,
      subscribed: c.subscribed,
    };
  }
  return map;
}

// PE-1607 — Contacts table "Recent Broadcasts" / "Recent Segments" filters.
// Resolves selected broadcast/segment CharityStack ids to the contact ids the
// table should show (union within a dimension, intersect across). The caller
// passes the result to the contacts query's `ids` filter. Returns [] when
// nothing is selected or nothing matches.
export async function getContactIdsForBroadcastsSegments(input: {
  broadcastIds?: string[];
  segmentIds?: string[];
}): Promise<string[]> {
  const broadcastIds = input.broadcastIds ?? [];
  const segmentIds = input.segmentIds ?? [];
  if (broadcastIds.length === 0 && segmentIds.length === 0) return [];
  const res: { contactIds: string[] } =
    await getMarketingClient().contacts.idsForBroadcastsSegments.query({
      ...(broadcastIds.length > 0 && { broadcastIds }),
      ...(segmentIds.length > 0 && { segmentIds }),
    });
  return res.contactIds ?? [];
}

// Which channel(s) a consent write targets. Mirrors the backend's
// `consentChannelSchema` (@charitystack/schemas/marketing/mirror). 'both' is the
// pre-per-channel global behavior and the back-compat default.
export type ConsentChannel = 'email' | 'sms' | 'both';

// PE-1608 / per-channel-consent Rev 4 (U10) — CS-initiated subscribe/unsubscribe
// from the Contacts surfaces. Bidirectional: subscribed=false opts out,
// subscribed=true re-subscribes. `channel` targets a single channel ('email' /
// 'sms') or 'both' (default — the pre-per-channel global behavior). The tRPC
// procedure defaults an omitted channel to 'both'; we send it explicitly.
export async function setContactSubscription(
  contactId: string,
  subscribed: boolean,
  channel: ConsentChannel = 'both',
): Promise<{
  contactId: string;
  pocketflowUnsubscribed: string;
  communicationConsent: boolean;
  emailConsent?: string;
  smsConsent?: string;
}> {
  return getMarketingClient().contacts.setSubscription.mutate({
    contactId,
    subscribed,
    channel,
  });
}

// PE-1870: a merchant admin attesting the donor separately confirmed texting
// START to the org's number after a carrier-level SMS STOP block. Distinct
// from setContactSubscription above — routes through the same use case but
// stamps a distinct, auditable provenance source (CONSENT_SOURCE_ADMIN_VERIFIED_START)
// so this deliberate override is never confused with a routine toggle click.
// Always SMS + subscribe=true (the only meaningful shape for this action).
export async function setContactSubscriptionAdminVerifiedStart(
  contactId: string,
): Promise<{
  contactId: string;
  pocketflowUnsubscribed: string;
  communicationConsent: boolean;
  emailConsent?: string;
  smsConsent?: string;
}> {
  return getMarketingClient().contacts.setSubscriptionAdminVerifiedStart.mutate({
    contactId,
  });
}

// Bulk subscribe/unsubscribe (Contacts table selection bar). One target state
// applied to many contacts (DDB + PF). Capped at 200 ids per call — the caller
// chunks larger selections. `channel` behaves as in setContactSubscription.
export async function setContactSubscriptionBulk(
  contactIds: string[],
  subscribed: boolean,
  channel: ConsentChannel = 'both',
): Promise<{
  results: Array<{ contactId: string; communicationConsent: boolean }>;
  updated: number;
  skipped: number;
}> {
  return getMarketingClient().contacts.setSubscriptionBulk.mutate({
    contactIds,
    subscribed,
    channel,
  });
}

// PE-1608 — per-broadcast un/resubscribe. `broadcastId` is the CharityStack
// mirror UUID each engagement row carries. Requires a synced contact.
export async function setBroadcastSubscription(
  contactId: string,
  broadcastId: string,
  subscribed: boolean,
): Promise<{ broadcastId: string; subscribed: boolean }> {
  return getMarketingClient().contacts.setBroadcastSubscription.mutate({
    contactId,
    broadcastId,
    subscribed,
  });
}

// M-7.1 — CS-initiated deletes. `id` is the CharityStack-owned UUID
// (the mirror table PK each dashboard row carries). The backend deletes
// the object in PocketFlows AND removes the local mirror row +
// contact-lookup rows. `deleted: false` means the row was already gone
// locally — callers treat both outcomes as success.
export async function deleteBroadcast(id: string): Promise<{ deleted: boolean }> {
  return getMarketingClient().broadcasts.delete.mutate({ id });
}

// M-7.1 — lifecycle. One verb pair covers both object types: the
// backend maps start/stop to PF's campaign start/stop or trigger
// activate/deactivate based on the row's broadcast_type. Returns the
// new PF status so callers update the row in place (OpenSearch lags).
export async function startBroadcast(id: string): Promise<{ status: PfBroadcastStatus }> {
  return getMarketingClient().broadcasts.start.mutate({ id });
}

export async function stopBroadcast(id: string): Promise<{ status: PfBroadcastStatus }> {
  return getMarketingClient().broadcasts.stop.mutate({ id });
}

// Read-repair: re-fetch the object from PF and upsert our mirror. PF
// fires NO webhook for status transitions (start/finish), so mirrors
// drift the moment a campaign sends. Called when a detail page opens.
export async function refreshBroadcast(id: string): Promise<{ status: PfBroadcastStatus }> {
  return getMarketingClient().broadcasts.refresh.mutate({ id });
}

// List-level read-repair — re-syncs EVERY broadcast in the org from PF
// (status + statistics). Called when the Broadcasts page loads.
// `changed` counts rows whose status differed, so callers know whether
// a refetch is worthwhile.
export async function refreshAllBroadcasts(): Promise<{
  refreshed: number;
  changed: number;
}> {
  return getMarketingClient().broadcasts.refreshAll.mutate({});
}

// Duplicate copies the campaign/trigger in PF, writes the mirror row
// synchronously, and returns the new row's identifiers so the caller
// can redirect straight to its edit page (carry via navigation state —
// don't refetch, OpenSearch lags).
export async function duplicateBroadcast(id: string): Promise<{
  id: string;
  pocketflowsId: string;
  name: string;
  broadcastType: PfBroadcastType;
  status: PfBroadcastStatus;
}> {
  return getMarketingClient().broadcasts.duplicate.mutate({ id });
}

export async function deleteSegment(id: string): Promise<{ deleted: boolean }> {
  return getMarketingClient().segments.delete.mutate({ id });
}

// Copies the segment in PF and writes the mirror row synchronously;
// returns the new row's identifiers for the redirect (carry via
// navigation state — OpenSearch lags).
export async function duplicateSegment(id: string): Promise<{
  id: string;
  pocketflowsId: string;
  name: string;
}> {
  return getMarketingClient().segments.duplicate.mutate({ id });
}

// Merchant account wrappers — fetch current Marketing tier provisioning
// status and toggle it on. Used by MarketingSettings.tsx to drive the
// "Enable Marketing" switch and the post-onboarding settings panel.
export async function getMerchantAccountStatus(): Promise<MerchantAccountStatus> {
  return getMarketingClient().merchantAccount.getStatus.query({});
}

export async function enableMarketing(): Promise<MerchantAccountStatus> {
  return getMarketingClient().merchantAccount.enableMarketing.mutate({});
}

export async function requestSmsNumber(
  // OPTIONAL — the contact phone lives in Settings → Organization now
  // (saved via saveMarketingPhone below); omitted means "use the stored
  // one". Passing it inline still works (legacy SMS-tab fallback branch).
  phoneNumber: string | undefined,
  // PE-1766 — preferred 3-digit area codes (validated server-side against
  // the shared US area-code dataset; the combobox only offers real codes).
  preferredPrefixes: string[],
): Promise<MerchantAccountStatus> {
  return getMarketingClient().merchantAccount.requestSmsNumber.mutate({
    ...(phoneNumber ? { phoneNumber } : {}),
    preferredPrefixes,
  });
}

// PE-1766 follow-up — saves the SMS registration contact phone from
// Settings → Organization (it sits next to the Primary contact there).
// Marketing owns the attribute; accounts-backend's nonprofit-settings
// deliberately never writes it, so this is its only save surface.
export async function saveMarketingPhone(
  phoneNumber: string,
): Promise<{ phoneNumber: string }> {
  return getMarketingClient().merchantAccount.saveMarketingPhone.mutate({
    phoneNumber,
  });
}

export async function disableMarketing(): Promise<MerchantAccountStatus> {
  return getMarketingClient().merchantAccount.disableMarketing.mutate({});
}

// PocketFlows embed URL — short-lived. FE re-fetches on every navigation
// to a Create page (broadcasts / segments / triggers). Per PF docs, the
// URL is pre-signed; we pass it directly to `<iframe src>`.
export type Embed = {
  id: string;
  url: string;
  expiresAt: string;
};

export async function createEmbed(): Promise<Embed> {
  return getMarketingClient().merchantAccount.createEmbed.mutate({});
}

// Re-export the client getter for advanced callers that want the proxy
// directly. Untyped — see file header.
export { getMarketingClient };

// PocketFlows template gallery (read-only) + "use this" instantiation.
// Templates are PF-managed, project-scoped, and authored by CharityStack in
// the PF dashboard; merchants only browse + instantiate. Types inlined per
// the file-header convention (dashboard does not import @charitystack/schemas).
export type MarketingTemplateChannel = 'email' | 'sms';
export type MarketingTemplate = {
  id: string;
  title: string;
  description: string;
  stepCount: number;
  channels: MarketingTemplateChannel[];
};
export type MarketingTriggerTemplate = MarketingTemplate & {
  triggerType: string | null;
};

export async function listMarketingTemplates(): Promise<{
  campaignTemplates: MarketingTemplate[];
  triggerTemplates: MarketingTriggerTemplate[];
}> {
  return getMarketingClient().templates.list.query({});
}

// Creates a DRAFT campaign/trigger in the caller's business from the chosen
// template, writes the mirror row, and returns its identifiers (incl. the CS
// `id`) so the caller can open the new draft's editor at /broadcasts/:id,
// carrying the row via navigation state — same flow as duplicateBroadcast.
export async function instantiateMarketingTemplate(input: {
  type: 'campaign' | 'trigger';
  templateId: string;
}): Promise<{
  id: string;
  pocketflowsId: string;
  broadcastType: PfBroadcastType;
  name: string;
  status: PfBroadcastStatus;
}> {
  return getMarketingClient().templates.instantiate.mutate(input);
}

// ── Donation Impact attribution (PE-1607 / PE-1519) ──────────────────────────
// All money fields are INTEGER CENTS, net of refunds. method is the strength
// tag the pipeline computed; the UI only displays it. Types inlined per the
// file-header convention (dashboard doesn't import @charitystack/schemas).
export type AttributionMethod = 'CONFIRMED' | 'LIKELY_INFLUENCED';

// NOTE: Surface 1 (the Transaction/Subscription sheet "Broadcast" section) no
// longer round-trips to attribution.transaction.query. DonationImpactSection now
// reads the denormalized broadcast fields already present on the donation row
// (broadcastId / broadcastName / broadcastType / attributionChannel), matching
// the payments table's Broadcast columns, so the section paints with the sheet
// instead of after a second fetch. The backend procedure still exists for other
// consumers.

export type AttributedDonationRow = {
  transactionId: string;
  donorName: string;
  donorEmail: string;
  amount: number; // cents, gross
  attributedAmount: number; // cents, net of refunds
  date: string; // ISO
  method: AttributionMethod;
  isRecurring: boolean;
};

export type BroadcastDonationsPage = {
  broadcastId: string;
  rollup: {
    donationsConfirmed: number;
    donationsInfluenced: number;
    revenueConfirmed: number; // cents
    revenueInfluenced: number; // cents
    staleDataAsOf: string | null;
  };
  weeklyBreakdown: Array<{
    weekStart: string;
    revenueConfirmed: number;
    revenueInfluenced: number;
    donationsConfirmed: number;
    donationsInfluenced: number;
  }>;
  donations: AttributedDonationRow[];
  nextCursor: string | null;
};

// Surface 2 — donations attributed to a broadcast (rollup + paginated list).
// `broadcastId` is the PocketFlows id (the mirror row's pocketflows_id).
export async function getBroadcastDonations(input: {
  broadcastId: string;
  cursor?: string | null;
  limit?: number;
}): Promise<BroadcastDonationsPage> {
  return getMarketingClient().attribution.broadcastDonations.query(input);
}

export type ContactTouch = {
  broadcastId: string;
  broadcastName: string;
  broadcastType: PfBroadcastType;
  channel: 'EMAIL' | 'SMS';
  sentAt: string; // ISO
  credited: boolean; // earned the Layer-2 attribution
};

export type TouchHistory = {
  transactionId: string;
  organizationId: string;
  donorName: string;
  donationAmount: number; // cents, net of refunds
  donationAt: string; // ISO
  touches: ContactTouch[];
};

// Surface 3 — per-donor marketing touch history leading up to a donation.
export async function getTouchHistory(transactionId: string): Promise<TouchHistory> {
  return getMarketingClient().attribution.touchHistory.query({ transactionId });
}

// Boundary types stay in the contracts workspace, never the backend tree.
export async function listAudiencePresets(): Promise<import('@charitystack/schemas').AudienceCatalog> {
  return getMarketingClient().segments.presets.query({});
}
export async function previewAudience(input: import('@charitystack/schemas').AudienceSelection): Promise<import('@charitystack/schemas').AudiencePreview> {
  return getMarketingClient().segments.previewPreset.query(input);
}
export async function createAudience(input: import('@charitystack/schemas').CreateAudienceInput): Promise<import('@charitystack/schemas').CreatedAudience> {
  return getMarketingClient().segments.createFromPreset.mutate(input, { context: { idempotencyKey: input.requestId } });
}
export async function createAudienceDraft(input: import('@charitystack/schemas').AudienceDraftInput): Promise<import('@charitystack/schemas').AudienceDraft> {
  return getMarketingClient().broadcasts.createAudienceDraft.mutate(input, { context: { idempotencyKey: input.requestId } });
}

// Native audience procedures deliberately parse every response at the browser
// boundary. The marketing proxy is untyped for legacy module-resolution
// reasons, so a TypeScript assertion here would otherwise turn a malformed or
// stale server response into trusted editor state.
// Segment templates and field options are opt-in, so every other caller keeps
// sending the input older backends accept.
type NativeAudienceCatalogOptions = { includeTemplates?: boolean; includeFieldOptions?: boolean };

function catalogInput(options: NativeAudienceCatalogOptions) {
  return {
    ...(options.includeTemplates ? { includeTemplates: true } : {}),
    ...(options.includeFieldOptions ? { includeFieldOptions: true } : {}),
  };
}

/**
 * True only for a backend deployed before `includeFieldOptions` existed: its
 * strict input schema answers BAD_REQUEST, and the marketing error formatter
 * carries the Zod issues only as a JSON string in the message. Anything else,
 * including a message that is not JSON, is a real error.
 */
export function isUnknownFieldOptionsKeyError(error: unknown): boolean {
  return isUnknownInputKeyError(error, 'includeFieldOptions');
}

/** The same check for any opt-in key an older strict backend doesn't know (ADM-227: `includeRejected`). */
export function isUnknownInputKeyError(error: unknown, key: string): boolean {
  if (!(error instanceof TRPCClientError)) return false;
  if ((error.data as { code?: unknown } | undefined)?.code !== 'BAD_REQUEST') return false;
  let issues: unknown;
  try { issues = JSON.parse(error.message); } catch { return false; }
  return Array.isArray(issues) && issues.some((issue) => {
    const candidate = issue as { code?: unknown; keys?: unknown } | null;
    return candidate?.code === 'unrecognized_keys' && Array.isArray(candidate.keys) && candidate.keys.includes(key);
  });
}

export async function getNativeAudienceCatalog(options: NativeAudienceCatalogOptions = {}) {
  const client = getMarketingClient();
  try {
    return nativeAudienceCatalogOutputSchema.parse(await client.segments.nativeCatalog.query(catalogInput(options)));
  } catch (error) {
    // Dashboard deployed before the backend: ask once more without the new key,
    // and pick-list fields fall back to free text (`fieldOptions` defaults to {}).
    if (!options.includeFieldOptions || !isUnknownFieldOptionsKeyError(error)) throw error;
    return nativeAudienceCatalogOutputSchema.parse(
      await client.segments.nativeCatalog.query(catalogInput({ ...options, includeFieldOptions: false })),
    );
  }
}

export async function loadNativeAudience(segmentId: string) {
  const input = audienceLoadInputSchema.parse({ segmentId });
  return nativeAudienceRecordSchema.parse(
    await getMarketingClient().segments.loadNative.query(input),
  );
}

// Exact segment counts (Segment Builder Redesign). An index problem comes back
// as `status: 'unavailable'`, so a thrown error here is a transport failure.
export async function previewSegmentExact(input: {
  targetToken: string;
  definition: NativeAudienceDefinition;
  /** Ask for the count of contacts Pocketflows refused (ADM-227); the builder's panel shows it. */
  includeRejected?: boolean;
}) {
  const parsedInput = segmentPreviewExactInputSchema.parse(input);
  const client = getMarketingClient();
  try {
    return segmentPreviewExactOutputSchema.parse(await client.segments.previewExact.query(parsedInput));
  } catch (error) {
    // Dashboard deployed before the backend (or a backend-only rollback): ask once more without
    // the new key. The result has no `rejected`, so the panel shows no can't-be-added line.
    if (!parsedInput.includeRejected || !isUnknownInputKeyError(error, 'includeRejected')) throw error;
    const { includeRejected: _omit, ...older } = parsedInput;
    void _omit;
    return segmentPreviewExactOutputSchema.parse(await client.segments.previewExact.query(older));
  }
}

export async function previewSegmentPeople(input: SegmentPreviewPeopleInput) {
  const parsedInput = segmentPreviewPeopleInputSchema.parse(input);
  return segmentPreviewPeopleOutputSchema.parse(
    await getMarketingClient().segments.previewPeople.query(parsedInput),
  );
}

export async function createNativeAudience(input: NativeAudienceCreateInput) {
  const parsedInput = nativeAudienceCreateInputSchema.parse(input);
  const result = nativeAudienceMutationResultSchema.parse(
    await getMarketingClient().segments.createNative.mutate(parsedInput, {
      context: { idempotencyKey: parsedInput.requestId },
    }),
  );
  if (result.requestId !== parsedInput.requestId) throw new Error('The segment save result did not match its request.');
  return result;
}

export async function updateNativeAudience(input: NativeAudienceUpdateInput) {
  const parsedInput = nativeAudienceUpdateInputSchema.parse(input);
  const result = nativeAudienceMutationResultSchema.parse(
    await getMarketingClient().segments.updateNative.mutate(parsedInput, {
      context: { idempotencyKey: parsedInput.requestId },
    }),
  );
  if (result.requestId !== parsedInput.requestId) throw new Error('The segment save result did not match its request.');
  return result;
}

export async function getNativeAudienceMembers(input: {
  segmentId: string;
  cursor?: string;
  staticOnly?: boolean;
}) {
  const parsedInput = audienceMembersInputSchema.parse(input);
  return audienceMembersOutputSchema.parse(
    await getMarketingClient().segments.membersNative.query(parsedInput),
  );
}
